import { Add } from "@mui/icons-material";
import { Card, Grid } from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import Box from "../../components/Box";
import Helpers from "../../commons/helpers";
import Button from "../../components/Button";
import Avatar from "../../components/Avatar";
import useStrings from "../../hooks/useStrings";
import ControlChip from "../../components/Chip";
import DataTable from "../../components/DataTable";
import FormField from "../../components/FormField";
import Typography from "../../components/Typography";
import Autocomplete from "../../components/Autocomplete";
import CustomStatusCell from "../../components/CustomStatusCell";
import useContainerOrganization from "../../hooks/useContainerOrganization";
import OrganizationService from "../../services/identity/organization.service";
import ModalHandelDeleteOrganization from "../../components/Organization/modalDeleteOrganization";

import { MainComponent } from "../main";
import { Mode, Status } from "../../constants/enums";
import { Constants } from "../../constants/constants";
import { useCommonComponentContext } from "../../providers/CommonComponentProvider";
import { ICodename, ILableOrganization, IPropListContainer } from "../../commons/interfaces";



export interface IRequestGetPagedOrg {
    orderby?: string;
    pageSize?: number;
    pageNumber?: number;
    totalCount?: number;
    searchText?: string;
    status?: string[];
    organizationCode?: string;
}

interface IModel {
    requestData?: IRequestGetPagedOrg;
    requestDataTemp?: IRequestGetPagedOrg;
}

type IProps = IPropListContainer<IRequestGetPagedOrg> & ILableOrganization & {
    title?: string,
    hidenActionUpdate?: boolean,
    hidenActionDelete?: boolean,
    hidenActionCreate?: boolean,
    hidenActionDeleteAllService?: boolean,
    onUpdateListOrganization: (props: { data: ICodename[], isFetchUserInfo: boolean }) => Promise<any>;
}

const OrganizationListContainer = ({
    title,
    lableGroup,
    lableStaff,
    lableOrganization,

    hidenActionUpdate,
    hidenActionDelete,
    hidenActionCreate,
    hidenActionDeleteAllService,

    requestGetPaged,

    onNavigate,
    onGetPaged,
    onUpdateListOrganization,
}: IProps) => {
    const strings = useStrings();

    const { handleChangeStatus } = useContainerOrganization();

    const {
        clientId, language, userInfo,
        onError, onSuccess, onShowLoading, onHideLoading,
    } = useCommonComponentContext();

    const { userProfile, listOrganization } = userInfo;

    const [dataRows, setDataRows] = useState<any[]>([]);
    const [model, setModel] = useState<IModel>({} as IModel);
    const [itemDelete, setItemDelete] = useState<any>(undefined);
    const [loadingDataTable, setLoadingDataTable] = useState<boolean>(false);

    const listStatus: ICodename[] = useMemo(
        () => [
            { code: `${Status.Active}`, name: strings.Common.ACTIVE },
            { code: `${Status.Inactive}`, name: strings.Common.IN_ACTIVE },
        ],
        [strings]
    );

    const newLableOrganization = useMemo(() => (
        (lableOrganization || strings.Common.ORGANIZATION).toLowerCase()
    ), [lableOrganization, strings]);

    const newLableStaff = useMemo(() => (
        (lableStaff || strings.Common.STAFF).toLowerCase()
    ), [lableStaff, strings]);

    const newLableGroup = useMemo(() => (
        (lableGroup || strings.Common.GROUP).toLowerCase()
    ), [lableGroup, strings]);

    useEffect(() => {
        const onStarted = async () => {
            const newReq: IRequestGetPagedOrg = {
                totalCount: requestGetPaged?.totalCount,
                status: requestGetPaged?.status || model?.requestData?.status,
                pageSize: requestGetPaged?.pageSize || model?.requestData?.pageSize,
                pageNumber: requestGetPaged?.pageNumber || model?.requestData?.pageNumber,
                searchText: requestGetPaged?.searchText || model?.requestData?.searchText,
                organizationCode: requestGetPaged?.organizationCode || model?.requestData?.organizationCode,
            };
            await getPaged(newReq);
        };
        onStarted();
    }, [requestGetPaged]);

    const getPaged = async (newReq?: IRequestGetPagedOrg) => {
        try {
            // onShowLoading();
            setLoadingDataTable(true);

            const pageSize = newReq?.pageSize || Constants.ROW_PER_PAGE;
            const pageNumber = Helpers.getPageNumber(newReq?.pageNumber || 1, pageSize, newReq?.totalCount || 0) || 1;

            const requestData: any = {
                pageSize: pageSize,
                pageNumber: pageNumber,

                clientId: clientId,
                status: newReq?.status,
                searchText: newReq?.searchText,
                organizationCode: newReq?.organizationCode,
            };

            const resultGetpaged = await OrganizationService.getPaged(requestData);

            let query: string = `?pageNumber=${requestData?.pageNumber}&pageSize=${requestData.pageSize}`;
            if (!Helpers.isNullOrEmpty(requestData?.organizationCode)) {
                query += `&organizationCode=${requestData?.organizationCode}`;
            }
            if (!Helpers.isNullOrEmpty(requestData?.searchText)) {
                query += `&searchText=${requestData?.searchText}`;
            }
            if ([...(requestData?.status || [])].length > 0) {
                [...(requestData?.status || [])].forEach((val) => {
                    query += `&status=${val}`;
                });
            }

            setDataRows(resultGetpaged.items || []);

            setModel({
                requestData: { ...requestData, totalCount: resultGetpaged.totalCount },
                requestDataTemp: { ...requestData, totalCount: resultGetpaged.totalCount },
            });

            onGetPaged({ query, totalCount: resultGetpaged.totalCount });
        } catch (error) {
            const e = Helpers.renderExceptionError(error);
            onError(e);
        } finally {
            // onHideLoading();
            setLoadingDataTable(false);
        }
    };

    const handleUpdateListOrganization = async (isDelete: boolean, row: any) => {
        let listOrgTemp = [...listOrganization || []];

        if (isDelete) {
            listOrgTemp = listOrgTemp.filter(e => `${e.code}` !== row.id);
        } else {
            const index = listOrgTemp.findIndex(e => (`${e.code}` === row.id));

            if (index === -1) {
                listOrgTemp.push({
                    code: row.id,
                    name: row.name,
                    detail: {
                        groups: [],
                        gene: row.gene,
                        isCollapse: false,
                        organizationCode: row.code,
                    }
                });
            }
        }

        onUpdateListOrganization({ data: listOrgTemp, isFetchUserInfo: (row.id === userProfile?.organizationId) });
    };

    const renderFilterForm = () => {
        return (
            <Box>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <FormField
                            maxLength={255}
                            defaultValue={model.requestDataTemp?.searchText}
                            label={strings.format(strings.Organization.SEARCH_TEXT, newLableOrganization)}
                            placeholder={strings.format(strings.Organization.ENTER_SEARCH_TEXT, newLableOrganization)}
                            onBlur={(value) => {
                                setModel(prev => ({
                                    ...prev,
                                    requestDataTemp: { ...prev.requestDataTemp, searchText: value },
                                }));
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Autocomplete
                            multiple
                            data={listStatus || []}
                            label={strings.Common.STATUS}
                            defaultValue={model.requestDataTemp?.status}
                            placeholder={strings.Organization.SELECT_STATUS}
                            onChange={(value) => {
                                setModel(prev => ({
                                    ...prev,
                                    requestDataTemp: { ...prev.requestDataTemp, status: value },
                                }));
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>
        );
    };

    const dataColumns = useMemo(() => (
        [
            {
                Header: Helpers.capitalizeFirstLetter(strings.format(strings.Organization.NAME, newLableOrganization)), accessor: "name", width: "250px",
                Cell: (row: any) => (
                    <Box display="flex" alignItems="center">
                        <Avatar
                            alt="avt-org"
                            src={row?.row?.original?.logoUrl}
                            text={Helpers.renderValueByLanguage({ data: row?.value, currentLanguage: language })}
                            sx={{
                                width: "36px",
                                height: "36px",
                                objectFit: "cover",
                                marginRight: "8px",
                                borderRadius: "50%",
                            }}
                        />
                        <Box display="grid">
                            <Typography
                                color="text"
                                variant="caption"
                                fontWeight="medium"
                            >
                                {row?.row?.original?.organizationCode}&nbsp;-&nbsp;
                                {Helpers.renderValueByLanguage({ data: row?.value, currentLanguage: language })}
                            </Typography>
                            {!Helpers.isNullOrEmpty(row?.row?.original?.phoneNumber) &&
                                <Typography
                                    color="secondary"
                                    variant="caption"
                                >
                                    sđt:&nbsp;{row?.row?.original?.phoneNumber || ""}
                                </Typography>
                            }
                            {!Helpers.isNullOrEmpty(row?.row?.original?.email) &&
                                <Typography
                                    color="secondary"
                                    variant="caption"
                                >
                                    email:&nbsp;{row?.row?.original?.email || ""}
                                </Typography>
                            }
                        </Box>
                    </Box>
                )
            },
            {
                Header: strings.format(strings.Organization.TOTAL_GROUP, newLableGroup), accessor: "groupNumber",
                Cell: (row: any) => (
                    <Typography
                        color="text"
                        variant="caption"
                        fontWeight="medium"
                    >
                        {row?.value || 0}
                    </Typography>
                )
            },
            {
                Header: strings.format(strings.Organization.TOTAL_STAFF, newLableStaff), accessor: "userNumber",
                Cell: (row: any) => (
                    <Typography
                        color="text"
                        variant="caption"
                        fontWeight="medium"
                    >
                        {row?.value || 0}
                    </Typography>
                )
            },
            {
                Header: strings.Common.STATUS, accessor: "status",
                Cell: (row: any) => <CustomStatusCell status={Number(row.value)} />,
            },
        ]
    ), [strings, language, newLableOrganization, newLableGroup, newLableStaff]);

    const actionList = (row: any): any => {
        return [
            {
                key: "detail",
                iconName: "view",
                title: strings.Common.VIEW_DETAIL,
                callback: (row: any) => {
                    onNavigate({ id: row.id, mode: Mode.View })
                },
            },

            !hidenActionUpdate &&
            // (row?.isAdmin === true || row?.isOwner === true) &&
            {
                key: "edit",
                iconName: "edit",
                title: strings.Common.EDIT,
                callback: (row: any) => {
                    onNavigate({ id: row.id, mode: Mode.Update });
                },
            },

            !hidenActionDelete &&
            // (row?.isAdmin === true || row?.isOwner === true) &&
            {
                key: "delete",
                iconName: "delete",
                title: strings.Common.DELETE,
                callback: (row: any) => { setItemDelete(row); },
            },

            !hidenActionUpdate &&
            // (row?.isAdmin === true || row?.isOwner === true) &&
            {
                key: "inactive",
                iconName: Number(row.status) === Status.Active ? "close" : "check",
                title: Number(row.status) === Status.Active ? strings.Common.IN_ACTIVE : strings.Common.ACTIVE,
                callback: (row: any) => {
                    handleChangeStatus({
                        row: row,
                        onCallBack: async () => {
                            await handleUpdateListOrganization(Number(row.status) === Status.Active, row);
                            await getPaged(model.requestData);
                        },
                    });
                },
            },
        ];
    };

    return (
        <MainComponent>
            <Grid container spacing={2}>
                {(!hidenActionCreate || !Helpers.isNullOrEmpty(title)) && (
                    <Grid item xs={12}>
                        <Box sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            justifyContent: !Helpers.isNullOrEmpty(title) ? "space-between" : "end",
                        }}>
                            {
                                !Helpers.isNullOrEmpty(title) &&
                                <Typography variant="h5">{title}</Typography>
                            }
                            {
                                !hidenActionCreate &&
                                <Button onClick={() => onNavigate({ mode: Mode.Create })}>
                                    <Add /> &nbsp; {strings.Common.ADD_NEW}
                                </Button>
                            }
                        </Box>
                    </Grid>
                )}

                <Grid item xs={12}>
                    {[...(model.requestData?.status || [])].length > 0 && (
                        <Box m={2}>
                            {[...(model.requestData?.status || [])].map((str) => (
                                <ControlChip
                                    label={strings.Common.STATUS}
                                    value={listStatus.find((el) => el.code === str)?.name}
                                    onDelete={() => {
                                        const listTemp = [...(model.requestData?.status || [])].filter((i) => i !== str);
                                        getPaged({ ...model.requestData, status: listTemp });
                                        // onGetPaged({
                                        //     totalCount: requestGetPaged?.totalCount,
                                        //     query: `?${Helpers.handleFormatParams({ ...model.requestData, status: listTemp })}`,
                                        // });
                                    }}
                                />
                            ))}
                        </Box>
                    )}
                    <Card>
                        <Box p={2}>
                            <DataTable
                                loading={loadingDataTable}
                                totalCount={model.requestData?.totalCount || 0}
                                pageNumber={model.requestData?.pageNumber || 1}
                                rowPerPage={model.requestData?.pageSize || Constants.ROW_PER_PAGE}
                                onChangePageSize={(pageSize) => {
                                    getPaged({ ...model.requestData, pageSize });
                                    // onGetPaged({
                                    //     totalCount: requestGetPaged?.totalCount,
                                    //     query: `?${Helpers.handleFormatParams({ ...model.requestData, pageSize })}`,
                                    // });
                                }}
                                onChangePageNumber={(pageNumber) => {
                                    getPaged({ ...model.requestData, pageNumber });
                                    // onGetPaged({
                                    //     totalCount: requestGetPaged?.totalCount,
                                    //     query: `?${Helpers.handleFormatParams({ ...model.requestData, pageNumber })}`,
                                    // });
                                }}
                                //
                                searchText={model.requestData?.searchText}
                                onSearch={(value) => {
                                    getPaged({ ...model.requestData, pageNumber: 1, searchText: value });
                                    // onGetPaged({
                                    //     totalCount: requestGetPaged?.totalCount,
                                    //     query: `?${Helpers.handleFormatParams({ ...model.requestData, pageNumber: 1, searchText: value })}`,
                                    // });
                                }}
                                placeholderSearch={strings.format(strings.Organization.SEARCH_TEXT, newLableOrganization)}
                                onSearchChange={(val) => {
                                    setModel(prev => ({ ...prev, requestData: { ...prev.requestData, searchText: val } }));
                                }}

                                filterForm={renderFilterForm()}
                                onFilter={() => {
                                    getPaged({ ...model.requestDataTemp, pageNumber: 1 });
                                    // onGetPaged({
                                    //     totalCount: requestGetPaged?.totalCount,
                                    //     query: `?${Helpers.handleFormatParams({ ...model.requestDataTemp, pageNumber: 1 })}`,
                                    // });
                                }}
                                onReset={() => {
                                    const temp: any = {
                                        pageNumber: 1,
                                        searchText: "",
                                        organizationCode: "",
                                        status: [],
                                    };

                                    getPaged(temp);
                                    // onGetPaged({
                                    //     totalCount: requestGetPaged?.totalCount,
                                    //     query: `?${Helpers.handleFormatParams(temp)}`,
                                    // });
                                }}
                                onCloseFilter={() => {
                                    setModel(prev => ({ ...prev, requestDataTemp: prev.requestData }));
                                }}
                                //
                                table={{
                                    rows: dataRows,
                                    columns: dataColumns,
                                }}
                                actionList={actionList}
                            />
                        </Box>
                    </Card>
                </Grid>
            </Grid>
            {
                !Helpers.isNullOrEmpty(itemDelete) &&
                <ModalHandelDeleteOrganization
                    lable={newLableOrganization}
                    hidenCheckBox={hidenActionDeleteAllService}
                    openDelete={!Helpers.isNullOrEmpty(itemDelete)}
                    params={{
                        id: itemDelete?.id,
                        clientId: clientId,
                        organizationId: userInfo?.userProfile?.organizationId,
                    }}
                    onCancle={() => {
                        setItemDelete(undefined);
                    }}
                    onOk={async () => {
                        setItemDelete(undefined);

                        await handleUpdateListOrganization(true, itemDelete);
                        await getPaged(model.requestData);
                    }}
                />
            }
        </MainComponent>
    );
};

export default OrganizationListContainer;